<script lang="ts">
	import { localeRound } from "#lib/utils/math.js";
	import { norwayHourLabel } from "#lib/utils/norwayHour.js";
	import type { Dag } from "#lib/types/spotPriser.js";

	type Props = {
		day: Dag;
	};
	let { day }: Props = $props();

	// Indexes of the cheapest and most-expensive hour. Picked by raw spot
	// price (the "real" market reading) rather than the with-strømstøtte
	// price, so the highlight stays stable regardless of whether strømstøtte
	// applies — and matches what PriceMinMax already calls min/max.
	const minIdx = $derived(
		day.priser.length === 0
			? -1
			: day.priser.reduce((bi, p, i, arr) => (p.pris < arr[bi].pris ? i : bi), 0)
	);
	const maxIdx = $derived(
		day.priser.length === 0
			? -1
			: day.priser.reduce((bi, p, i, arr) => (p.pris > arr[bi].pris ? i : bi), 0)
	);

	// Em-dash marker for hours without strømstøtte — same convention as
	// PriceMinMax.
	const NA = "—";
	const fmt = (n: number) => localeRound(n);
</script>

<table
	class="table table-fixed shadow-none [--table-padding-y:var(--s-1)] [--table-padding-x:var(--s-3)]"
>
	<thead>
		<tr>
			<th>kl.</th>
			<th class="text-end whitespace-nowrap">øre/kWh</th>
			<th class="text-end hyphens-manual">med strøm&shy;støtte</th>
		</tr>
	</thead>
	<tbody>
		{#each day.priser as p, i (p.validOnUtc ?? p.tidspunkt)}
			<tr class:row-low={i === minIdx} class:row-high={i === maxIdx}>
				<td class="tabular-nums">{norwayHourLabel(p.validOnUtc)}</td>
				<td class="text-end tabular-nums">{fmt(p.pris)}</td>
				<td class="text-end tabular-nums">
					{p.hasStromstottePris != null ? fmt(p.hasStromstottePris) : NA}
				</td>
			</tr>
		{/each}
	</tbody>
</table>

<style lang="postcss">
	/* Cheapest / most-expensive hour markers — light brand tint as the row
	   background, dark brand-tinted text on top. The text mix is heavily
	   toward `--natt` (70 %) so the tint is just a hint of hue and the
	   actual readable mass is dark — keeps WCAG AA contrast against the
	   light tinted background. Tune the two percentages to taste. */
	.row-low {
		background: color-mix(in oklab, var(--skog) 12%, transparent);
		color: color-mix(in oklab, var(--skog) 30%, var(--natt));
	}

	.row-high {
		background: color-mix(in oklab, var(--naust) 12%, transparent);
		color: color-mix(in oklab, var(--naust) 30%, var(--natt));
	}
</style>
